/*
 * This file is part of glowingblue/orell-bookshelf.
 *
 * Copyright (c) 2021 Priska Wermelinger & Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component, { type ComponentAttrs } from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import type Mithril from 'mithril';
import type BookshelvesListState from '../states/BookshelvesListState';
import BookshelfSort from './BookshelfSort';
import BookshelvesListItem from './BookshelvesListItem';
import BookshelvesListItemEmpty from './BookshelvesListItemEmpty';
import redrawWhenSettled from '../utils/redrawWhenSettled';

export interface BookshelvesListAttrs extends ComponentAttrs {
	state: BookshelvesListState;
}

export default class BookshelvesList<CustomAttrs extends BookshelvesListAttrs = BookshelvesListAttrs> extends Component<CustomAttrs> {
	view(): Mithril.Children {
		const state = this.attrs.state;

		let loading: Mithril.Children;

		if (state.isInitialLoading() || state.isLoadingNext()) {
			loading = <LoadingIndicator />;
		} else if (state.hasNext()) {
			loading = (
				<Button className="Button" onclick={state.loadNext.bind(state)}>
					{app.translator.trans('core.forum.discussion_list.load_more_button')}
				</Button>
			);
		}

		const showUser: boolean | undefined = state.getParams().showAll;

		return (
			<div className={`BookshelvesList ${showUser ? 'showUser' : ''}`}>
				<BookshelfSort state={state} prfx="glowingblue-orell-bookshelf.forum.bookshelves.sort" />
				<div className="BookshelvesList--items-container">
					{state.getPages().map((pg) => {
						return pg.items.map((bookshelf) => <BookshelvesListItem bookshelf={bookshelf} showUser={showUser} />);
					})}
					{state.isEmpty() && <BookshelvesListItemEmpty onCreated={() => redrawWhenSettled(state.refresh())} />}
				</div>

				<div className="BookshelvesList-loadMore">{loading}</div>
			</div>
		);
	}
}
